import React from 'react';
import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { formatLargeNumber, formatNumber, secondsToHumanReadable } from '../../utils/utils';

/**
 * Hero KPI strip for the Signals page. Six metric tiles laid out
 * horizontally, each with a soft tone-keyed background and a big
 * tabular-number value. Designed to be the first thing the user
 * lands on - one glance and they know the shape of their data.
 *
 * Props:
 *   metrics: {
 *     totalSignals, totalRecords, durationSeconds,
 *     patternedCount, anomalousCount, highRelevanceCount,
 *     firstTimestamp, lastTimestamp,
 *   }
 */

const Tile = ({ value, label, sub, tone = 'primary', big = false }) => {
  const theme = useTheme();
  const color = (() => {
    switch (tone) {
      case 'success': return theme.palette.success.main;
      case 'error':   return theme.palette.error.main;
      case 'warning': return theme.palette.warning.main;
      case 'info':    return theme.palette.info.main;
      case 'neutral': return theme.palette.text.secondary;
      case 'primary':
      default:        return theme.palette.primary.main;
    }
  })();

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        position: 'relative',
        px: 2.25,
        py: 1.75,
        borderRadius: 2,
        background: `linear-gradient(135deg,
          ${alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.08)} 0%,
          ${alpha(color, theme.palette.mode === 'dark' ? 0.04 : 0.02)} 100%)`,
        border: `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.28 : 0.18)}`,
        overflow: 'hidden',
        // Subtle inner glow at the top edge for the "raised" feel.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${alpha(color, 0.6)}, transparent)`,
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Inter", "SF Pro Display", system-ui, sans-serif',
          fontSize: big ? 28 : 24,
          fontWeight: 700,
          lineHeight: 1.05,
          color: theme.palette.text.primary,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          mt: 0.25,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: color,
        }}
      >
        {label}
      </Typography>
      {sub && (
        <Typography
          sx={{
            fontSize: 10,
            color: theme.palette.text.secondary,
            mt: 0.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
};

const fmtTimestamp = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
  } catch (_e) { return null; }
};

// Compact duration: the two largest units only (e.g. "5y 2mo", "3d 4h"), so
// the Time Span tile stays one short line instead of wrapping to a paragraph.
// The full, precise breakdown lives in the tooltip.
const compactDuration = (sec) => {
  if (!sec || sec <= 0) return '—';
  const units = [['y', 31536000], ['mo', 2592000], ['d', 86400], ['h', 3600], ['m', 60], ['s', 1]];
  const parts = [];
  let rem = Math.floor(sec);
  for (const [label, size] of units) {
    if (rem >= size) {
      parts.push(`${Math.floor(rem / size)}${label}`);
      rem %= size;
      if (parts.length === 2) break;
    }
  }
  return parts.length ? parts.join(' ') : '0s';
};

const SignalsKpiStrip = ({ metrics }) => {
  const {
    totalSignals = 0,
    totalRecords = 0,
    durationSeconds = 0,
    patternedCount = 0,
    anomalousCount = 0,
    highRelevanceCount = 0,
    firstTimestamp,
    lastTimestamp,
  } = metrics || {};

  const anomalyPct = totalSignals > 0 ? (anomalousCount / totalSignals) * 100 : 0;
  const relevancePct = totalSignals > 0 ? (highRelevanceCount / totalSignals) * 100 : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr 1fr',
          sm: '1fr 1fr 1fr',
          md: 'repeat(6, 1fr)',
        },
        gap: 1.25,
        mb: 2.5,
      }}
    >
      <Tile
        value={formatNumber(totalSignals)}
        label="Signals"
        sub="distinct columns"
        tone="primary"
      />
      <Tile
        value={formatLargeNumber(totalRecords) || formatNumber(totalRecords)}
        label="Records"
        sub="across all signals"
        tone="info"
        big
      />
      <Tooltip
        arrow placement="top"
        title={
          (firstTimestamp || lastTimestamp || durationSeconds > 0) ? (
            <Box sx={{ fontSize: 11 }}>
              {durationSeconds > 0 && <Box>{secondsToHumanReadable(durationSeconds)}</Box>}
              {firstTimestamp && <Box>From: {fmtTimestamp(firstTimestamp)}</Box>}
              {lastTimestamp && <Box>To: {fmtTimestamp(lastTimestamp)}</Box>}
            </Box>
          ) : ''
        }
      >
        <Box sx={{ display: 'flex' }}>
          <Tile
            value={compactDuration(durationSeconds)}
            label="Time Span"
            sub={fmtTimestamp(lastTimestamp) ? `latest ${fmtTimestamp(lastTimestamp)}` : null}
            tone="success"
          />
        </Box>
      </Tooltip>
      <Tile
        value={formatNumber(patternedCount)}
        label="Patterned"
        sub="seasonal / cyclic"
        tone="success"
      />
      <Tile
        value={formatNumber(anomalousCount)}
        label="Anomalous"
        sub={anomalyPct > 0 ? `${anomalyPct.toFixed(1)}% of signals` : 'none flagged'}
        tone="error"
      />
      <Tile
        value={formatNumber(highRelevanceCount)}
        label="High Relevance"
        sub={relevancePct > 0 ? `${relevancePct.toFixed(0)}% are ≥80%` : 'awaiting analysis'}
        tone="warning"
      />
    </Box>
  );
};

// Memoized: ``metrics`` is a useMemo'd object in the parent, so unrelated
// parent re-renders (selection, dialogs) skip this strip entirely.
export default React.memo(SignalsKpiStrip);
